import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import clsx from 'clsx';
import { Icon } from '../../../layout/Icon.jsx';
import { formatIQD } from '../../../shared/format.js';
import { useCreateTransfer } from '../hooks/useTransfers.js';

export function TransferForm({ open, onClose, cashboxes = [] }) {
  const create = useCreateTransfer();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm({
    defaultValues: { fromCashboxId: '', toCashboxId: '', amount: 0, note: '' }
  });

  useEffect(() => {
    if (open) reset({ fromCashboxId: '', toCashboxId: '', amount: 0, note: '' });
  }, [open, reset]);

  const fromId = watch('fromCashboxId');
  const toId = watch('toCashboxId');
  const amount = Number(watch('amount') || 0);

  const fromCb = useMemo(
    () => cashboxes.find((c) => (c._id || c.id) === fromId),
    [cashboxes, fromId]
  );

  const fromBalance = fromCb?.balance ?? fromCb?.currentBalance ?? 0;

  if (!open) return null;

  async function submit(values) {
    clearErrors();
    if (!values.fromCashboxId || !values.toCashboxId) return;
    if (values.fromCashboxId === values.toCashboxId) {
      setError('toCashboxId', { type: 'manual', message: 'يجب أن تختلف القاصة المرسلة عن المستقبلة' });
      return;
    }
    const amt = Number(values.amount);
    if (!amt || amt <= 0) {
      setError('amount', { type: 'manual', message: 'المبلغ مطلوب' });
      return;
    }
    if (amt > fromBalance) {
      setError('amount', { type: 'manual', message: 'الرصيد غير كافٍ في القاصة المصدر' });
      return;
    }
    await create.mutateAsync(
      {
        fromCashboxId: values.fromCashboxId,
        toCashboxId: values.toCashboxId,
        amount: amt,
        note: values.note?.trim() || ''
      },
      { onSuccess: () => onClose?.() }
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'rgba(0,0,0,.45)' }}
    >
      <form
        className="card w-[480px] max-w-full"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <div className="card-h">
          <h3 className="text-[15px] font-bold flex-1">تحويل بين القاصات</h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="card-b space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">من قاصة *</label>
              <select
                className={clsx('input', errors.fromCashboxId && 'border-red')}
                {...register('fromCashboxId', { required: true })}
              >
                <option value="">اختر...</option>
                {cashboxes.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
                ))}
              </select>
              {fromCb ? (
                <div className="text-[11px] text-ink-3 mt-1">
                  الرصيد: <b style={{ color: '#C2570D' }}>{formatIQD(fromBalance)}</b>
                </div>
              ) : null}
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">إلى قاصة *</label>
              <select
                className={clsx('input', errors.toCashboxId && 'border-red')}
                {...register('toCashboxId', { required: true })}
              >
                <option value="">اختر...</option>
                {cashboxes
                  .filter((c) => (c._id || c.id) !== fromId)
                  .map((c) => (
                    <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
                  ))}
              </select>
              {errors.toCashboxId?.message ? (
                <div className="text-red text-[11.5px] mt-1 font-semibold">{errors.toCashboxId.message}</div>
              ) : null}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">المبلغ (د.ع) *</label>
            <input
              className={clsx('input', errors.amount && 'border-red')}
              type="number"
              min="0"
              step="100"
              {...register('amount', { valueAsNumber: true })}
            />
            {errors.amount?.message ? (
              <div className="text-red text-[11.5px] mt-1 font-semibold">{errors.amount.message}</div>
            ) : amount > 0 && fromCb && amount <= fromBalance ? (
              <div className="text-[11px] text-ink-3 mt-1">
                الرصيد بعد التحويل: <b>{formatIQD(fromBalance - amount)}</b>
              </div>
            ) : null}
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظة</label>
            <textarea className="input" rows={2} {...register('note')} />
          </div>
        </div>

        <div className="card-h border-b-0 border-t flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose} disabled={create.isPending}>
            إلغاء
          </button>
          <button type="submit" className="btn btn-primary" disabled={create.isPending}>
            {create.isPending ? 'جاري...' : 'تنفيذ التحويل'}
          </button>
        </div>
      </form>
    </div>
  );
}
